// Org chart — bulk manager editor + minimal chart view. tickets/sonnet.md
// "org chart: UI (admin editor screen + chart view)". Opened from the
// /people page header (pages/people.js). Server half already shipped
// (bulk-read + admin bulk-write on PartyRelationship); this is the
// remaining UI. Data/state logic lives in functions/orgChart.js.
//
// Chart tab (view = 'chart', default) is open to any workspace member —
// GET /core/parties/relationships is member-gated server-side, matching
// "anyone can see who reports to whom". Edit tab is admin-only: the tab
// button is hidden for non-admins, and the edit view's own content is
// ALSO gated (defense in depth against a stale/forced view state) — the
// server enforces the real boundary (POST .../reports-to is
// requireWorkspaceManager), this is belt-and-suspenders on the client.
const _reset = {
  _orgChartView: 'chart',
  _orgChartParties: [],
  _orgChartEdges: [],
  _orgChartWorking: {},
  _orgChartLoading: false,
  _orgChartSaving: false,
  _orgChartLoadError: null,
  _orgChartSaveError: null
}

export const OrgChartModal = {
  extends: 'Modal',
  stateKey: '_orgChartOpen',
  extraClose: _reset,

  Window: {
    // The centre sheet (DESIGN_SPEC §6.1) at `overlayLg` — a tree and a
    // row editor, wider than a form; capped by the veil's inset box. The
    // shared Modal owns the veil, the sheet motion, the phone sheet and the
    // focus/Escape contract.
    width: 'overlayLg',
    maxWidth: '100%',
    maxHeight: '100%',
    overflow: 'auto',
    flow: 'y',
    gap: 'A',

    ModalHeader: {
      Hgroup: {
        H: { text: '{{ workspace.orgChart.title | polyglot }}' },
        P: {
          text: '{{ workspace.orgChart.subtitle | polyglot }}'
        }
      }
    },

    // Chart · Edit is a VIEW switch — the kit's segmented control in its
    // `view` strength (the raised thumb), not two hand-made tabs on raw
    // `overlayStrong` washes. Edit is admin-only, and one segment is no
    // choice at all, so the whole bar shows to admins only (a member sees the
    // chart and nothing to switch).
    TabBar: {
      extends: 'AppSegmented',
      variant: 'view',
      hide: (el, s) => !el.call('canAdmin', s),
      options: [
        { value: 'chart', label: 'workspace.orgChart.tabChart' },
        { value: 'edit', label: 'workspace.orgChart.tabEdit' }
      ],
      childProps: {
        isActive: (el, s) =>
          (s.root?._orgChartView === 'edit' ? 'edit' : 'chart') === el.optionValue,
        ariaPressed: (el, s) =>
          (s.root?._orgChartView === 'edit' ? 'edit' : 'chart') === el.optionValue
            ? 'true'
            : 'false',
        text: (el) => el.call('polyglot', el.optionLabel || ''),
        onClick: (e, el, s) => s.root.update({ _orgChartView: el.optionValue })
      }
    },

    // §9 loading law (DESIGN-LOCKS-RED-1): geometry-reserving skeleton rows,
    // never a renderable "Loading…" string.
    LoadingRows: {
      hide: (el, s) => !s.root?._orgChartLoading,
      flow: 'y',
      gap: 'V',
      Row_1: { extends: 'SkeletonRow', width: '60%' },
      Row_2: { extends: 'SkeletonRow', width: '90%' },
      Row_3: { extends: 'SkeletonRow', width: '75%' }
    },
    LoadErrorNote: {
      hide: (el, s) => !s.root?._orgChartLoadError,
      tag: 'p',
      role: 'alert',
      margin: '0',
      fontSize: 'Z2',
      color: 'dangerInk',
      text: (el, s) => s.root?._orgChartLoadError || ''
    },

    // ── Chart tab — read-only tree, open to any member ─────────────────────
    ChartView: {
      hide: (el, s) =>
        s.root?._orgChartView === 'edit' ||
        s.root?._orgChartLoading ||
        !!s.root?._orgChartLoadError,
      flow: 'y',
      gap: 'Z',
      overflow: 'auto',
      maxHeight: '52vh',

      EmptyNote: {
        hide: (el, s) => !!el.call('orgChartDirectory').length,
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'caption',
        text: '{{ workspace.orgChart.empty | polyglot }}'
      },
      Tree: {
        flow: 'y',
        gap: 'A',
        childExtends: 'OrgChartTreeNode',
        childrenAs: 'state',
        children: (el, s) => {
          const byId = new Map(el.call('orgChartDirectory').map((p) => [p.partyId, p]))
          const rootIds = el.call('orgChartChildrenOf').get('root') || []
          return rootIds
            .map((id) => byId.get(id))
            .filter(Boolean)
            .map((p) => ({ ...p, depth: 0 }))
        }
      }
    },

    // ── Edit tab — admin-only bulk manager editor ───────────────────────────
    EditView: {
      hide: (el, s) =>
        s.root?._orgChartView !== 'edit' ||
        s.root?._orgChartLoading ||
        !!s.root?._orgChartLoadError ||
        !el.call('canAdmin', s),
      flow: 'y',
      gap: 'Z',

      RowList: {
        flow: 'y',
        gap: '-',
        overflow: 'auto',
        maxHeight: '46vh',
        childExtends: 'OrgChartEditRow',
        childrenAs: 'state',
        children: (el) => el.call('orgChartDirectory')
      },

      SaveErrorNote: {
        hide: (el, s) => !s.root?._orgChartSaveError,
        tag: 'p',
        role: 'alert',
        margin: '0',
        fontSize: 'Z2',
        color: 'dangerInk',
        text: (el, s) => s.root?._orgChartSaveError || ''
      },

      Footer: {
        flow: 'x',
        gap: 'Z',
        align: 'center',
        justifyContent: 'flex-end',

        // The kit's ink primary (DESIGN_SPEC §6.9, L68): the `primary` theme,
        // its own washes and the disabled step — never an opacity (Rule 65);
        // the house floor for a label that changes while it works.
        AppCapsuleBtnPrimary: {
          minWidth: 'E',
          disabled: (el, s) => !el.call('orgChartIsDirty') || !!s.root?._orgChartSaving,
          text: (el, s) =>
            el.call(
              'polyglot',
              s.root?._orgChartSaving ? 'workspace.orgChart.saving' : 'workspace.orgChart.save'
            ),
          onClick: async (e, el, s) => {
            e.preventDefault()
            const ok = await el.call('orgChartSave', s)
            if (ok) {
              el.call('openNotification', {
                title: el.call('polyglot', 'workspace.orgChart.saved'),
                message: el.call('polyglot', 'workspace.orgChart.savedBody'),
                type: 'success'
              })
            }
          }
        }
      }
    }
  }
}
